// apps/mtd-itsa-web/src/components/application/hmrc/hmrc-connect-dialog.tsx
import { useState, ReactNode } from 'react';
import { Button } from '@/components/base/buttons/button';
import { DialogTrigger, ModalOverlay, Modal, Dialog } from '@/components/application/modals/modal';
import { Input } from '@/components/base/input/input';
import { useAuth } from '@/providers/auth-provider';
import { useProject } from '@/providers/project-provider';
import { useHandleError } from '@/hooks/use-handle-error';

export type HmrcUserType = 'individual' | 'agent';

interface HmrcConnectDialogProps {
    trigger: ReactNode;
    onConnect: (userType: HmrcUserType, projectId: string) => Promise<void>;
    isConnecting: boolean;
}

type Step = 'select-type' | 'enter-details';

export const HmrcConnectDialog = ({
    trigger,
    onConnect,
    isConnecting,
}: HmrcConnectDialogProps) => {
    const { user } = useAuth();
    const { projects, loading: projectsLoading } = useProject();
    const handleError = useHandleError();

    const [step, setStep] = useState<Step>('select-type');
    const [selectedUserType, setSelectedUserType] = useState<HmrcUserType | null>(null);

    // Form State
    const [accountName, setAccountName] = useState('');
    const [nino, setNino] = useState('');
    const [utr, setUtr] = useState('');

    const [isCreatingProject, setIsCreatingProject] = useState(false);
    // Local validation errors (shown inline in the form)
    const [validationError, setValidationError] = useState<string | null>(null);

    const resetState = () => {
        setStep('select-type');
        setSelectedUserType(null);
        setAccountName('');
        setNino('');
        setUtr('');
        setValidationError(null);
    };

    // Validate form inputs
    const validateInputs = (): boolean => {
        setValidationError(null);

        if (!accountName.trim()) {
            setValidationError('Account name is required');
            return false;
        }

        if (!nino.trim()) {
            setValidationError('National Insurance Number is required');
            return false;
        }

        // Basic NINO format validation (e.g., QQ123456A)
        const ninoPattern = /^[A-Z]{2}\s?\d{2}\s?\d{2}\s?\d{2}\s?[A-D]$/i;
        if (!ninoPattern.test(nino.trim())) {
            setValidationError('Please enter a valid National Insurance Number (e.g., QQ 12 34 56 A)');
            return false;
        }

        if (!utr.trim()) {
            setValidationError('Unique Taxpayer Reference is required');
            return false;
        }

        // Basic UTR validation (10 digits)
        const utrPattern = /^\d{10}$/;
        if (!utrPattern.test(utr.trim().replace(/\s/g, ''))) {
            setValidationError('UTR must be 10 digits');
            return false;
        }

        return true;
    };

    const handleTypeSelection = async (type: HmrcUserType, close: () => void) => {
        if (projectsLoading) return;

        if (!user?.id) {
            handleError('User not authenticated', 'Account Connection');
            return;
        }

        try {
            if (type === 'individual') {
                const existingProject = projects.find(p => p.type === 'mtditsa');

                if (existingProject) {
                    // Existing project found - connect immediately
                    await onConnect(type, existingProject.id);
                    close();
                    setTimeout(resetState, 300);
                } else {
                    // No project found - proceed to form
                    setSelectedUserType(type);
                    setStep('enter-details');
                }
            } else if (type === 'agent') {
                // Agents can create multiple projects
                setSelectedUserType(type);
                setStep('enter-details');
            }
        } catch (error) {
            handleError(error, 'Failed to process account type selection');
        }
    };

    const handleProjectCreation = async (close: () => void) => {
        // Validate inputs first
        if (!validateInputs()) {
            return;
        }

        if (!selectedUserType || !user?.id) {
            handleError('Missing required information', 'Project Creation');
            return;
        }

        try {
            setIsCreatingProject(true);
            setValidationError(null);

            const apiUrl = import.meta.env.VITE_API_URL;

            if (!apiUrl) {
                throw new Error('API URL is not configured. Please contact support.');
            }

            // Create new project
            const response = await fetch(`${apiUrl}/api/projects/create-mtd-project`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_uuid: user.id,
                    account_name: accountName.trim(),
                    nino: nino.trim().replace(/\s/g, ''), // Remove spaces from NINO
                    utr: utr.trim().replace(/\s/g, ''), // Remove spaces from UTR
                    user_type: selectedUserType,
                }),
            });

            if (!response.ok) {
                let errorMessage = 'Failed to create project';

                try {
                    const errData = await response.json();
                    errorMessage = errData.error || errData.message || errorMessage;
                } catch (parseError) {
                    // If response is not JSON, use status text
                    errorMessage = `${errorMessage} (${response.status}: ${response.statusText})`;
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();

            if (!data.project_id) {
                throw new Error('No project ID returned from server');
            }

            // Connect with new project ID
            await onConnect(selectedUserType, data.project_id);

            // Close dialog and reset
            close();
            setTimeout(resetState, 300);

        } catch (err) {
            // Use the error handling hook for system/API errors
            handleError(err, 'Failed to create MTD ITSA project');

            // Also show a user-friendly inline message
            if (err instanceof Error) {
                setValidationError(err.message);
            } else {
                setValidationError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsCreatingProject(false);
        }
    };

    return (
        <DialogTrigger>
            {trigger}
            <ModalOverlay isDismissable={!isConnecting && !isCreatingProject}>
                <Modal>
                    <Dialog>
                        {({ close }) => (
                            <div className="w-full max-w-md rounded-2xl bg-primary p-6 shadow-xl">
                                {/* HEADER */}
                                <div className="mb-6">
                                    <h2 className="text-lg font-semibold text-primary">
                                        {step === 'select-type' ? 'Who are you connecting as?' : 'Account Details'}
                                    </h2>
                                    <p className="mt-1 text-sm text-tertiary">
                                        {step === 'select-type'
                                            ? 'Choose whether you are connecting as the taxpayer or as a tax agent.'
                                            : 'Please provide the details for this new MTD account.'}
                                    </p>
                                </div>

                                {/* STEP 1: SELECT TYPE */}
                                {step === 'select-type' && (
                                    <div className="flex flex-col gap-3">
                                        <Button
                                            size="md"
                                            color="primary"
                                            className="w-full"
                                            disabled={isConnecting || projectsLoading}
                                            onClick={() => handleTypeSelection('individual', close)}
                                        >
                                            {projectsLoading ? 'Checking...' : "I'm an Individual taxpayer"}
                                        </Button>
                                        <Button
                                            size="md"
                                            color="secondary"
                                            className="w-full"
                                            disabled={isConnecting || projectsLoading}
                                            onClick={() => handleTypeSelection('agent', close)}
                                        >
                                            {projectsLoading ? 'Checking...' : "I'm a Tax Agent"}
                                        </Button>
                                    </div>
                                )}

                                {/* STEP 2: ENTER DETAILS */}
                                {step === 'enter-details' && (
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">
                                                Account Name <span className="text-error-primary">*</span>
                                            </label>
                                            <Input
                                                placeholder="e.g. My Personal Tax"
                                                value={accountName}
                                                onChange={(value) => {
                                                    setAccountName(value);
                                                    setValidationError(null); // Clear error on input
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">
                                                National Insurance Number (NINO) <span className="text-error-primary">*</span>
                                            </label>
                                            <Input
                                                placeholder="QQ 12 34 56 A"
                                                value={nino}
                                                onChange={(value) => {
                                                    setNino(value);
                                                    setValidationError(null);
                                                }}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-primary">
                                                Unique Taxpayer Reference (UTR) <span className="text-error-primary">*</span>
                                            </label>
                                            <Input
                                                placeholder="1234567890"
                                                value={utr}
                                                onChange={(value) => {
                                                    setUtr(value);
                                                    setValidationError(null);
                                                }}
                                            />
                                        </div>

                                        {validationError && (
                                            <div className="text-sm text-error-primary bg-error-primary/10 p-3 rounded-md border border-error-primary/20">
                                                {validationError}
                                            </div>
                                        )}

                                        <Button
                                            size="md"
                                            color="primary"
                                            className="w-full mt-2"
                                            disabled={isCreatingProject || isConnecting}
                                            onClick={() => handleProjectCreation(close)}
                                        >
                                            {isCreatingProject ? 'Creating Project...' : 'Continue to HMRC'}
                                        </Button>
                                    </div>
                                )}

                                {/* FOOTER */}
                                <div className="mt-6 flex justify-between items-center">
                                    {step === 'enter-details' ? (
                                        <button
                                            type="button"
                                            className="text-sm text-tertiary hover:text-primary underline transition-colors"
                                            onClick={() => {
                                                setStep('select-type');
                                                setValidationError(null);
                                            }}
                                            disabled={isCreatingProject}
                                        >
                                            Back
                                        </button>
                                    ) : <div />}

                                    <Button
                                        size="sm"
                                        color="secondary"
                                        type="button"
                                        onClick={() => {
                                            close();
                                            setTimeout(resetState, 300);
                                        }}
                                        disabled={isCreatingProject || isConnecting}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </DialogTrigger>
    );
};
